import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getStore, Store, getStoreUpcomingRides, StoreRide } from '../../services/storeService';
import { trackStoreView } from '../../utils/clubStoreAnalytics';
import toast from 'react-hot-toast';
import { MapPinIcon, HomeIcon, PhoneIcon, EnvelopeIcon, WrenchScrewdriverIcon, CalendarIcon, UserGroupIcon } from '@heroicons/react/24/outline';

// Service labels for display
const SERVICE_LABELS: Record<string, string> = {
  bike_repair: 'Bike Repair & Tune-ups',
  bike_fitting: 'Professional Bike Fitting',
  wheel_building: 'Custom Wheel Building',
  suspension_service: 'Suspension Service',
  drivetrain_service: 'Drivetrain Service',
  brake_service: 'Brake Service',
  tubeless_setup: 'Tubeless Setup',
  bike_rental: 'Bike Rental',
  group_rides: 'Group Rides',
  coaching: 'Cycling Coaching',
};

const StoreDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [store, setStore] = useState<Store | null>(null);
  const [upcomingRides, setUpcomingRides] = useState<StoreRide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (storeId) {
      loadStoreDetails();
    }
  }, [storeId]);

  useEffect(() => {
    if (store && user?.id) {
      trackStoreView(store.id, 'StoreDetails', user.role || undefined).catch(console.error);
    }
  }, [store, user?.id]);

  const loadStoreDetails = async () => {
    if (!storeId) return;
    
    try {
      setError(null);
      const [storeData, ridesData] = await Promise.all([
        getStore(storeId),
        getStoreUpcomingRides(storeId),
      ]);
      setStore(storeData);
      setUpcomingRides(ridesData);
    } catch (err: any) {
      console.error('Error fetching store details:', err);
      setError(err.message || t('common.error') || 'Failed to load store details');
      toast.error(err.message || t('common.error') || 'Failed to load store details');
      navigate('/discover/stores');
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const handlePhoneClick = () => {
    if (store?.phone) {
      window.location.href = `tel:${store.phone}`;
    }
  };

  const handleEmailClick = () => {
    if (store?.email) {
      window.location.href = `mailto:${store.email}`;
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">{t('common.loading') || 'Loading store details...'}</div>
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || t('stores.storeNotFound') || 'Store not found'}</p>
          <button
            onClick={loadStoreDetails}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 mr-2"
          >
            {t('common.retry') || 'Retry'}
          </button>
          <button
            onClick={() => navigate('/discover/stores')}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            {t('common.back') || 'Back to Stores'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{store.name}</h1>
            {store.location && (
              <div className="flex items-center text-gray-600">
                <MapPinIcon className="h-5 w-5 mr-2" />
                <span>{store.location}</span>
              </div>
            )}
          </div>
          {store.isActive && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              {t('common.active') || 'Active'}
            </span>
          )}
        </div>
      </div>

      {/* Store Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('stores.information') || 'Information'}</h2>
        
        <div className="space-y-4">
          {store.city && (
            <div className="flex items-start">
              <MapPinIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <div className="text-sm text-gray-500">{t('stores.city') || 'City'}</div>
                <div className="text-base text-gray-900">{store.city}</div>
              </div>
            </div>
          )}

          {store.address && (
            <div className="flex items-start">
              <HomeIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <div className="text-sm text-gray-500">{t('stores.address') || 'Address'}</div>
                <div className="text-base text-gray-900">{store.address}</div>
              </div>
            </div>
          )}

          {store.phone && (
            <div className="flex items-start">
              <PhoneIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm text-gray-500">{t('stores.phone') || 'Phone'}</div>
                <button
                  onClick={handlePhoneClick}
                  className="text-base text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {store.phone}
                </button>
              </div>
            </div>
          )}

          {store.email && (
            <div className="flex items-start">
              <EnvelopeIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm text-gray-500">{t('stores.email') || 'Email'}</div>
                <button
                  onClick={handleEmailClick}
                  className="text-base text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {store.email}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Services Section */}
      {store.services && store.services.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <WrenchScrewdriverIcon className="h-6 w-6 mr-2 text-primary-600" />
            {t('stores.services') || 'Services'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {store.services.map((service) => (
              <div
                key={service}
                className="flex items-center p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-primary-600 mr-2">•</span>
                <span className="text-gray-700">{SERVICE_LABELS[service] || service}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Rides Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <CalendarIcon className="h-6 w-6 mr-2 text-primary-600" />
          {t('stores.upcomingRides') || 'Upcoming Rides'}
        </h2>
        {upcomingRides.length > 0 ? (
          <div className="space-y-4">
            {upcomingRides.map((ride) => (
              <Link
                key={ride.id}
                to={`/rides/${ride.id}`}
                className="block p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">{ride.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {formatDate(ride.departureDate)}
                      {ride.departureTime && ` at ${ride.departureTime}`}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">{ride.departureLocation}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-primary-600">{ride.distanceKm} km</div>
                    <div className="flex items-center text-sm text-gray-500 mt-1">
                      <UserGroupIcon className="h-4 w-4 mr-1" />
                      {ride._count.participants}/{ride.maxRiders}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">
            {t('stores.noUpcomingRides') || 'No upcoming rides'}
          </p>
        )}
      </div>
    </div>
  );
};

export default StoreDetailsPage;
